/**
 * 작성자 : 홍선영
 * 날짜 : 2024.08.26
 * 기능 : 대시보드 & 상황판 무선 유해가스 현황 위젯
 */

import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v1, v4 } from 'uuid';
import { arraySortByAscdOrder } from '../../utils/arraySortByAscdOrder';
import styled from 'styled-components';
// eslint-disable-next-line import/no-extraneous-dependencies
import Carousel from 'react-multi-carousel';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'react-multi-carousel/lib/styles.css';
import { CustomDot } from './Carousel';
import IssueGuide from '../IssueGuide';
import GetFetchStatus from '../../hooks/GetFetchStatus';
import SkeletonTable from '../SkeletonTable';
import { ERROR, O2_RANGE } from '../../_constants';
import { userState } from '../../atoms';
import { useRecoilValue } from 'recoil';
import { apiGet } from '../../services/_common';
import { useQuery } from '@tanstack/react-query';
import { ISensorRange } from 'customTypes';
import { convertCO2PpmToPercent, convertCO2PpmToMgPerCubicMeter } from '../../utils/convertCO2Unit';

type Props = {
  autoRefresh?: boolean;
  autoPlay?: boolean;
  valueType?: 'shape' | 'number';
  size?: 'small' | 'large';
  uniqueKey: string;
  refetchInterval?: number;
};

const MGasTable = styled.div`
  flex-grow: 1;
  display: flex;
  width: 100%;
  overflow: visible;
  .tableHeader,
  .tableBody {
    padding-bottom: 0.75rem;
  }
  .tableHeader {
    padding-left: 0.5rem;
    display: flex;
    flex-direction: column;
    flex: 0 0 9rem;
    > div {
      padding: 0 1rem;
      gap: 0.25rem;
    }
    .sensorName {
      display: flex;
      align-items: center;
      flex: 0 0 3.5rem;
      color: var(--text-secondary);
      font-size: 0.875rem;
      .material-symbols-rounded {
        color: var(--text-tertiary);
      }
    }
    .sensors {
      font-size: 0.875rem;
      color: var(--text-secondary);
      padding: 0 0.75rem;
      width: 9rem;

      .counter {
        color: var(--text-tertiary);
        background-color: var(--tonal);
        padding: 0 0.5rem;
        border-radius: 0.5rem;
        margin-right: 0.25rem;
      }
    }
  }
  .tableBody {
    padding-right: 0.5rem;
    display: flex;
    width: calc(100% - 9.5rem);
    flex-grow: 1;
    overflow: hidden;
    position: relative;
    .carousel-container {
      width: 100%;
    }
    .background-rows {
      position: absolute;
      width: 100%;
      height: 100%;
      padding-right: 0.5rem;
      padding-bottom: 0.75rem;
      pointer-events: none;
    }
    .react-multi-carousel-track {
      height: 100%;
      cursor: grab;
      &:active {
        cursor: grabbing;
      }
      .react-multi-carousel-item {
        display: flex;
        flex-direction: column;
      }
    }
    .area {
      flex: 1 0 8rem;
      display: flex;
      flex-direction: column;
      .areaHeader {
        text-align: center;
        font-size: 0.875rem;
        flex: 0 0 3.5rem;
        color: var(--text-primary);
        .pointLabel {
          display: flex;
        }
      }
      .pointSide {
        display: flex;
        flex: 1 0 10rem;
        .pointDetail {
          flex: 1 0 4rem;
          display: flex;
          flex-direction: column;
          .mgasValue {
            justify-content: center;
          }
        }
      }
    }
  }
  .sensors,
  .mgasValue {
    flex: 1 0 2.125rem;
    display: flex;
    align-items: center;
    border-top: 1px solid var(--outline);
    .removeBackground {
      background-color: transparent !important;
      font-size: 1.375rem !important;
    }
  }

  .react-multi-carousel-dot-list {
    bottom: -6px;
  }
`;

const WGasInfo = ({ autoRefresh, autoPlay = false, valueType = 'shape', size = 'small', uniqueKey, refetchInterval }: Props) => {
  const { t } = useTranslation();
  const { hCd, sCd } = useRecoilValue(userState);

  const fetchSensorData = async () => {
    try {
      const req = { hCd, sCd };
      const res = await apiGet({ path: '/sensor/wgas/widget', req });
      const result = getMergedData(res.data.data);
      return result;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const fetchUlist = async () => {
    try {
      const req = { hCd, sCd, subCd: '09' };
      const res = await apiGet({ path: '/sensor/ulist', req });
      const useYList = res.data.data.sensorUList.filter((el: any) => el.useYn === 'Y' && el.uListYn === 'Y');
      const result = arraySortByAscdOrder(useYList, 'etc1');
      return result;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const fetchAllSensorRanges = async () => {
    try {
      const res = await apiGet({ path: '/sensor/range', req: { hCd, sCd } });
      const rangeMap: { [key: string]: { level1: number; level2: number; level3: number; unit?: string } } = {};
      res.data.data.rangeList.forEach((list: ISensorRange) => {
        rangeMap[list.srCd] = {
          level1: list.level1 ? Number(list.level1) : 0,
          level2: list.level2 ? Number(list.level2) : 0,
          level3: list.level3 ? Number(list.level3) : 0,
          unit: list.unit,
        };
      });

      // 체감온도 측정 범위는 하드코딩 적용
      rangeMap.FEELS = {
        level1: 31,
        level2: 33,
        level3: 35,
        unit: '°C',
      };

      return rangeMap;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const {
    data: ulistData,
    isLoading: isLoadingUlistData,
    isFetching: isFetchingUlistData,
    isRefetching: isRefetchingUlistData,
    isError: isErrorUlistData,
    refetch: refetchUlist,
  } = useQuery([`sensorUListGet_${uniqueKey}`, hCd, sCd], () => fetchUlist(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    ...(refetchInterval && { refetchInterval }),
  });

  const {
    data: sensorData,
    isLoading: isLoadingSensorData,
    isFetching: isFetchingSensorData,
    isRefetching: isRefetchingSensorData,
    isError: isErrorSensorData,
    refetch: refetchSensor,
  } = useQuery([`tareaSensorGet_${uniqueKey}`, hCd, sCd], () => fetchSensorData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    ...(refetchInterval && { refetchInterval }),
  });

  const {
    data: allSensorRanges,
    isLoading: isLoadingAllSensorRanges,
    isFetching: isFetchingAllSensorRanges,
  } = useQuery([`allSensorRangesGet_${uniqueKey}`, hCd, sCd], () => fetchAllSensorRanges(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    ...(refetchInterval && { refetchInterval }),
    cacheTime: 0,
  });

  const getMergedData = (arr: any[]) => {
    const result: WgasMergeType[] = [];
    arr?.forEach((obj) => {
      const existingItem = result.find((el) => el.sub_cd === obj.sub_cd);
      if (!existingItem) result.push(obj);
    });
    return result;
  };

  useEffect(() => {
    const autoRefreshWidget = () => {
      refetchSensor();
      refetchUlist();
    };
    if (autoRefresh) {
      const interval = setInterval(autoRefreshWidget, 60 * 1000);
      return () => clearInterval(interval);
    }
    return () => {};
  }, [autoRefresh]);

  // 유해가스목록 갯수에 따라 carousel에 display할 아이템갯수 구하는 함수
  const calculateCarouselItemCount = useCallback(() => {
    if (sensorData) {
      if (size === 'large') {
        if (sensorData.length <= 5) return sensorData.length;
        return 6;
      }
      {
        if (sensorData.length <= 3) return sensorData.length;
        return 3;
      }
    }
    return 6;
  }, [size, sensorData]);

  const carouselCount = calculateCarouselItemCount();

  const responsive = useMemo(
    () => ({
      desktop: {
        breakpoint: { max: 3000, min: 1024 },
        items: carouselCount,
        slidesToSlide: carouselCount, // optional, default to 1.
      },
      tablet: {
        breakpoint: { max: 1024, min: 464 },
        items: carouselCount,
        slidesToSlide: carouselCount, // optional, default to 1.
      },
      mobile: {
        breakpoint: { max: 464, min: 0 },
        items: 1,
        slidesToSlide: 1, // optional, default to 1.
      },
    }),
    [size, sensorData]
  );

  // 새로고침 버튼 클릭 시
  const onClickRefresh = () => {
    refetchSensor();
    refetchUlist();
  };

  // ssCd(센서코드) => index 반환
  const getSensorIndexByType = (type: string): number => {
    switch (type) {
      case 'O2':
        return 1;
      case 'NO2':
        return 2;
      case 'CO':
        return 3;
      case 'CO2':
        return 4;
      case 'H2S':
        return 5;
      case 'CH4':
        return 6;
      case 'CH2O':
        return 7;
      case 'O3':
        return 8;
      case 'PM2.5':
        return 9;
      case 'PM10':
        return 10;
      case 'TEMP':
        return 11;
      case 'HUMI':
        return 12;
      case 'VOC':
        return 13;
      case 'PM1':
        return 14;
      case 'FEELS':
        return 15;
      default:
        return 0;
    }
  };

  // CO2 ppm 값을 unit에 따라 변환하는 함수
  const convertCO2Value = (ppmValue: number | string | undefined): number => {
    if (ppmValue === undefined || ppmValue === null || ppmValue === '') {
      return 0;
    }

    const numValue = typeof ppmValue === 'string' ? Number(ppmValue) : ppmValue;
    if (isNaN(numValue)) {
      return 0;
    }

    const range = allSensorRanges?.CO2;
    if (!range?.unit) {
      return numValue;
    }

    const { unit } = range;
    switch (unit) {
      case '%':
        return convertCO2PpmToPercent(numValue);
      case 'mg/m³':
      case 'mg':
        return convertCO2PpmToMgPerCubicMeter(numValue);
      case 'ppm':
      default:
        return numValue;
    }
  };

  // 센서 값과 range를 비교하여 level을 계산하는 함수
  const calculateLevel = (sensorCode: string, sensorValue: number | string | undefined): string => {
    if (!allSensorRanges || sensorValue === undefined || sensorValue === null || sensorValue === '') {
      return '';
    }

    const numValue = typeof sensorValue === 'string' ? Number(sensorValue) : sensorValue;
    if (isNaN(numValue)) {
      return '';
    }

    // VOC 경우 기본적으로 컬러 강제 반환 [ 0: 좋음, 1~2: 경고, 3: 위험 ]
    if (sensorCode === 'VOC') {
      const vocValue = Number(sensorValue);

      if (vocValue === 0) return '0';
      if (vocValue === 1) return '1';
      if (vocValue === 2) return '2';
      if (vocValue === 3) return '3';
      return '0';
    }

    const range = allSensorRanges[sensorCode];
    if (!range || !range.level1 || !range.level2 || !range.level3) {
      return '';
    }

    const { level1, level2, level3 } = range;
    const valueToCompare = Number(numValue);

    // 산소 경우 범위 계산
    if (sensorCode === 'O2') {
      const matchedRange = [...O2_RANGE].reverse().find((o2Range) => {
        const [lowerMin, lowerMax] = o2Range.lower;
        const [upperMin, upperMax] = o2Range.upper;

        // 하한선(lower) 체크: -1은 무시, 값이 범위 내에 있는지 확인
        const isLowerBreached = (lowerMin !== -1 && valueToCompare < lowerMin) || (lowerMax !== -1 && valueToCompare <= lowerMax);

        // 상한선(upper) 체크: -1은 무시
        const isUpperBreached = (upperMin !== -1 && valueToCompare > upperMin) || (upperMax !== -1 && valueToCompare >= upperMax);

        return isLowerBreached || isUpperBreached;
      });

      return matchedRange ? String(matchedRange.level) : '0';
    }

    // CO2의 경우 변환된 값으로 비교
    if (sensorCode === 'CO2' && range.unit) {
      const co2Value = convertCO2Value(numValue);
      if (co2Value < level1) return '0';
      if (co2Value >= level1 && co2Value < level2) return '1';
      if (co2Value >= level2 && co2Value < level3) return '2';
      return '3';
    }

    // level0 (좋음): 값 < level1
    // level1 (주의): level1 <= 값 < level2
    // level2 (경고): level2 <= 값 < level3
    // level3 (위험): level3 <= 값
    if (valueToCompare < level1) {
      return '0';
    }
    if (valueToCompare >= level1 && valueToCompare < level2) {
      return '1';
    }
    if (valueToCompare >= level2 && valueToCompare < level3) {
      return '2';
    }
    return '3';
  };

  const renderList = ({ data, sensorListData }: { data: any; sensorListData: any[] }) => {
    // valueType이 shape 인지 여부
    const valueTypeStatus = valueType === 'shape';
    // valueType이 shape가 아닌 경우 'removeBackground' 반환
    const removeBackground = valueTypeStatus ? 'removeBackground' : '';

    const getValue = (props: any) => {
      const getSensorIndex = getSensorIndexByType(props);
      // 레벨 값이 0, 1, 2, 3 중 하나인지 여부
      const isShapeValue = (level: string) => ['0', '1', '2', '3'].includes(level);
      // 데이터 값이 NC, null, '' 중 하나인지 여부
      const isDataValueEmpty = (dataProp: string | null) => dataProp === 'NC' || dataProp === null || dataProp === '';

      // valueType이 shape일 때
      if (valueTypeStatus) {
        const dataProp = `data${getSensorIndex}`;
        const dataValue = data[dataProp];

        if (!dataValue || dataValue === 'NC' || dataValue === null || dataValue === '') {
          return '-';
        }

        // 센서 코드 가져오기
        const sensorCode = props;
        if (!sensorCode) {
          return '-';
        }

        // 프론트에서 level 계산
        const levelValue = calculateLevel(sensorCode, dataValue);
        return isShapeValue(levelValue) ? '●' : '-';
      }

      // valueType이 number일 때
      const dataProp = `data${getSensorIndex}`;

      // CO2의 경우 단위에 따른 값 변환
      if (props === 'CO2') {
        return isDataValueEmpty(data[dataProp]) ? '-' : Number(convertCO2Value(data[dataProp])).toLocaleString();
      }
      // 데이터 값이 NC, null일 때 -
      return isDataValueEmpty(data[dataProp]) ? '-' : Number(data[dataProp]).toLocaleString();
    };

    // 레벨 값으로 색상 지정
    const getClassName = (etcParam: string) => {
      const sensorIndex = getSensorIndexByType(etcParam);
      const dataProp = `data${sensorIndex}`;
      const dataValue = data[dataProp]; // 해당 센서의 밸류 값

      if (!dataValue || dataValue === 'NC' || dataValue === null || dataValue === '') {
        return 'slate';
      }

      // 센서 코드 가져오기
      const sensorCode = etcParam;
      if (!sensorCode) {
        return 'slate';
      }

      // 프론트에서 level 계산
      const levelValue = calculateLevel(sensorCode, dataValue);

      if (levelValue === '0') return 'blue';
      if (levelValue === '1') return 'green';
      if (levelValue === '2') return 'amber';
      if (levelValue === '3') return 'red';
      return 'slate';
    };

    return (
      <div className='pointDetail'>
        {sensorListData.map((sensor: any) => {
          return (
            <div key={sensor.ssCd} className='mgasValue'>
              <span className={`textLight ${getClassName(sensor.ssCd)} ${removeBackground}`}>{getValue(sensor.ssCd)}</span>
              {/* <span className={`textLight ${getClassName(sensor.etc1)} ${removeBackground}`}>{getValue(sensor.etc1)}</span> */}
            </div>
          );
        })}
      </div>
    );
  };

  const loading =
    !sensorData ||
    !ulistData ||
    !allSensorRanges ||
    isLoadingSensorData ||
    isLoadingUlistData ||
    isLoadingAllSensorRanges ||
    isFetchingUlistData ||
    isFetchingSensorData ||
    isFetchingAllSensorRanges ||
    isRefetchingUlistData ||
    isRefetchingSensorData;
  if (isErrorSensorData || isErrorUlistData) return <IssueGuide type='error' text={ERROR} />;

  return (
    <div className='mGas tempEdit stickyHeader' style={{ overflowX: 'hidden' }}>
      <div className='widget-header margin-top'>
        <div className='widget-title flex-between innerBtn'>
          <span>{t('무선 유해가스 종합 현황')}</span>
          <div className='labelSet flex-end'>
            <span className='textBox blue'>{t('좋음')}</span>
            <span className='textBox green'>{t('주의')}</span>
            <span className='textBox amber'>{t('경고')}</span>
            <span className='textBox red'>{t('위험')}</span>
          </div>
          {!autoRefresh && (
            <div className='widget-btn-group'>
              <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
                <span className='material-symbols-rounded'>refresh</span>
              </div>
            </div>
          )}
        </div>
      </div>
      {loading ? (
        <div style={{ width: '100%', height: '100%' }}>
          <SkeletonTable />
        </div>
      ) : (
        <MGasTable style={autoPlay && ulistData.length >= 8 ? { animation: 'slide 15s infinite linear' } : { animation: 'none' }}>
          <div className='tableHeader'>
            <div className='sensorName'>
              <span className='material-symbols-rounded'>sensors</span>
              {t('센서명')}
            </div>
            {ulistData?.map((el: any, idx: number) => (
              <div className='sensors' key={el.ssCd}>
                <span className='counter'>{idx + 1}</span>
                {el.cdName}
              </div>
            ))}
          </div>
          <div className='tableBody'>
            <Carousel
              swipeable
              draggable
              responsive={responsive}
              infinite
              autoPlay={autoPlay && sensorData.length > carouselCount}
              autoPlaySpeed={15000}
              keyBoardControl
              transitionDuration={1000}
              containerClass='carousel-container'
              removeArrowOnDeviceType={['tablet', 'mobile', 'desktop']}
              dotListClass='custom-dot-list-style'
              showDots={sensorData.length > carouselCount}
              customDot={<CustomDot mergedData={sensorData} />}
            >
              {sensorData.map((el: WgasMergeType) => (
                <div key={v1()} className='area'>
                  <div className='areaHeader flex-center'>{el.s_name}</div>
                  <div className='pointSide'>{renderList({ data: el, sensorListData: ulistData })}</div>
                </div>
              ))}
            </Carousel>
            <div className='background-rows area'>
              <div className='areaHeader' />
              <div className='pointSide'>
                <div className='pointDetail'>
                  {ulistData.map(() => (
                    <div className='mgasValue' key={v4()} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </MGasTable>
      )}
    </div>
  );
};

export default WGasInfo;
